import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, Loader2, Pencil } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CreateCertificateRequestPayload } from "@/services/certificateRequestsService";

const CERTIFICADOS_EMAIL = "certificados@fiscalo.uy";

export interface FiscaloCertificateConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  certificateRequestPayload: CreateCertificateRequestPayload;
  documentNumber: string | null;
  documentType: "national_id" | "passport" | null;
  rut: string | null;
  legalName: string | null;
  tradeName: string | null;
  defaultPhone: string | null;
  /** Fire-and-forget: creates the cert request + kicks off the Abitab
   *  automation. Resolves once the cert request exists server-side
   *  (the automation call is best-effort, no polling). Returns:
   *  - "submitted": cert request was created. Automation may or may
   *    not have started, but a Fiscalo operator / cron can complete
   *    the trámite if the automator was down. Page refresh during
   *    this state is safe — sessionStorage is stamped immediately,
   *    so sync-on-refresh routes past viewStep 5 without retrying. */
  onConfirm: (
    phone: string | undefined,
    tradeName: string | undefined,
  ) => Promise<"submitted" | "failed">;
  confirming?: boolean;
  /** When non-null, modal shows the error state instead of the data
   *  review. Only "failed" remains relevant now that we don't poll
   *  for status. */
  failureMode?: "failed" | null;
  /** Reset failureMode and re-show the review form so the user can
   *  resubmit. */
  onRetry?: () => void;
}

/**
 * Editorial review-and-confirm modal for the Fiscalo cert acquisition
 * path. Surfaced from viewStep 5 (paso 03 — Certificado) so the user
 * reviews the data that will be POSTed to ID Digital before we actually
 * submit anything. Visual language matches the surrounding wizard: mono
 * eyebrow, serif heading, paper-2 grouped review, sage accent CTA.
 */
export function FiscaloCertificateConfirmModal({
  open,
  onOpenChange,
  certificateRequestPayload,
  documentNumber,
  documentType,
  rut,
  legalName,
  tradeName,
  defaultPhone,
  onConfirm,
  confirming = false,
  failureMode = null,
  onRetry,
}: FiscaloCertificateConfirmModalProps): JSX.Element {
  const { t } = useTranslation();
  const [phone, setPhone] = useState(defaultPhone?.trim() ?? "");
  const [tradeNameValue, setTradeNameValue] = useState(
    (tradeName ?? legalName ?? "").trim(),
  );

  const documentTypeLabel =
    documentType === "passport"
      ? t(
          "onboarding.certificateAcquisition.fiscaloConfirm.documentTypePassport",
        )
      : t(
          "onboarding.certificateAcquisition.fiscaloConfirm.documentTypeCi",
        );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!confirming) onOpenChange(o);
      }}
    >
      <DialogContent
        className="flex max-h-[90dvh] max-w-xl flex-col border-0 p-0"
        style={{
          background: "var(--fx-s-overlay)",
          borderRadius: "var(--fx-r-lg, 16px)",
          overflow: "hidden",
        }}
      >
        {/* Header — stays pinned. Eyebrow + title + subtitle swap to a
            progress framing while the automation runs in the background
            so the user understands the modal hasn't stalled. */}
        <div style={{ padding: "26px 28px 14px", flexShrink: 0 }}>
          <div
            style={{
              fontFamily: "var(--fx-ff-mono)",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              color: "var(--fx-ink-500, #6B7280)",
              marginBottom: 8,
            }}
          >
            {failureMode
              ? t(
                  "onboarding.certificateAcquisition.fiscaloConfirm.modalEyebrowFailed",
                  "No pudimos completar",
                )
              : confirming
                ? t(
                    "onboarding.certificateAcquisition.fiscaloConfirm.modalEyebrowProcessing",
                    "Procesando",
                  )
                : t(
                    "onboarding.certificateAcquisition.fiscaloConfirm.modalEyebrow",
                    "Revisión final",
                  )}
          </div>
          <DialogTitle asChild>
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--fx-ff-serif)",
                fontWeight: 500,
                fontSize: 24,
                lineHeight: 1.25,
                color: "var(--fx-s-text)",
              }}
            >
              {failureMode ? (
                <>
                  {t(
                    "onboarding.certificateAcquisition.fiscaloConfirm.modalFailedTitleLead",
                    "No pudimos enviar",
                  )}{" "}
                  <span className="ital" style={{ fontStyle: "italic" }}>
                    {t(
                      "onboarding.certificateAcquisition.fiscaloConfirm.modalFailedTitleAccent",
                      "tu solicitud.",
                    )}
                  </span>
                </>
              ) : confirming ? (
                <>
                  {t(
                    "onboarding.certificateAcquisition.fiscaloConfirm.modalProcessingTitleLead",
                    "Enviando tu solicitud",
                  )}{" "}
                  <span className="ital" style={{ fontStyle: "italic" }}>
                    {t(
                      "onboarding.certificateAcquisition.fiscaloConfirm.modalProcessingTitleAccent",
                      "a ID Digital.",
                    )}
                  </span>
                </>
              ) : (
                <>
                  {t(
                    "onboarding.certificateAcquisition.fiscaloConfirm.modalTitleLead",
                    "Revisá los datos",
                  )}{" "}
                  <span className="ital" style={{ fontStyle: "italic" }}>
                    {t(
                      "onboarding.certificateAcquisition.fiscaloConfirm.modalTitleAccent",
                      "que enviamos a ID Digital.",
                    )}
                  </span>
                </>
              )}
            </h2>
          </DialogTitle>
          <p
            style={{
              margin: "8px 0 0",
              fontSize: 13.5,
              lineHeight: 1.55,
              color: "var(--fx-ink-500, #6B7280)",
            }}
          >
            {failureMode === "failed"
              ? t(
                  "onboarding.certificateAcquisition.fiscaloConfirm.modalFailedSubtitle",
                  "No pudimos guardar la solicitud. Probá de nuevo en unos segundos — si sigue fallando, escribinos y un agente se ocupa.",
                )
              : confirming
                ? t(
                    "onboarding.certificateAcquisition.fiscaloConfirm.modalProcessingSubtitle",
                    "Estamos enviando tu solicitud a Fiscalo. Esto toma un segundo.",
                  )
                : t(
                    "onboarding.certificateAcquisition.fiscaloConfirm.modalSubtitle",
                    "Podés ajustar el nombre público y el celular si hace falta. El resto sale tal cual figura en DGI.",
                  )}
          </p>
        </div>

        {failureMode ? (
          <FiscaloAutomationFailure />
        ) : confirming ? (
          <FiscaloAutomationProgress />
        ) : (
        /* Review group — scrollable region inside the fixed-height
            modal, so on small viewports the user can still reach the
            bottom rows + actions. */
        <div
          style={{
            margin: "0 28px",
            border: "1px solid var(--fx-ink-200)",
            borderRadius: 12,
            background: "var(--fx-s-raised-hover)",
            overflow: "auto",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <ReadOnlyRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.documentType",
            )}
            value={documentTypeLabel}
          />
          <ReadOnlyRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.documentNumber",
            )}
            value={documentNumber ?? "—"}
          />
          <ReadOnlyRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.rut",
            )}
            value={rut ?? "—"}
          />
          <ReadOnlyRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.legalName",
            )}
            value={legalName ?? tradeName ?? "—"}
          />
          <EditableRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.tradeName",
            )}
            htmlFor="fiscalo-confirm-tradeName-modal"
          >
            <input
              id="fiscalo-confirm-tradeName-modal"
              type="text"
              value={tradeNameValue}
              onChange={(e) => setTradeNameValue(e.target.value)}
              placeholder={t(
                "onboarding.certificateAcquisition.fiscaloConfirm.tradeNamePlaceholder",
              )}
              disabled={confirming}
              style={inputStyles}
            />
          </EditableRow>
          <ReadOnlyRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.firstName",
            )}
            value={certificateRequestPayload.firstName}
          />
          {certificateRequestPayload.secondName ? (
            <ReadOnlyRow
              label={t(
                "onboarding.certificateAcquisition.fiscaloConfirm.secondName",
              )}
              value={certificateRequestPayload.secondName}
            />
          ) : null}
          <ReadOnlyRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.firstLastName",
            )}
            value={certificateRequestPayload.firstLastName}
          />
          {certificateRequestPayload.secondLastName ? (
            <ReadOnlyRow
              label={t(
                "onboarding.certificateAcquisition.fiscaloConfirm.secondLastName",
              )}
              value={certificateRequestPayload.secondLastName}
            />
          ) : null}
          <ReadOnlyRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.email",
            )}
            value={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {CERTIFICADOS_EMAIL}
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t(
                          "onboarding.certificateAcquisition.fiscaloConfirm.emailLegend",
                        )}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          cursor: "help",
                          color: "var(--fx-ink-500)",
                          display: "inline-flex",
                        }}
                      >
                        <Info
                          aria-hidden
                          style={{ width: 14, height: 14 }}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-[280px] text-xs leading-relaxed"
                    >
                      {t(
                        "onboarding.certificateAcquisition.fiscaloConfirm.emailLegend",
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
            }
          />
          <EditableRow
            label={t(
              "onboarding.certificateAcquisition.fiscaloConfirm.phoneLabel",
            )}
            htmlFor="fiscalo-confirm-phone-modal"
            isLast
          >
            <input
              id="fiscalo-confirm-phone-modal"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t(
                "onboarding.certificateAcquisition.fiscaloConfirm.phonePlaceholder",
              )}
              disabled={confirming}
              style={inputStyles}
            />
          </EditableRow>
        </div>
        )}

        {/* Actions — stays pinned at the bottom */}
        <div
          style={{
            padding: "20px 28px 26px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
            flexShrink: 0,
            borderTop: "1px solid color-mix(in srgb, var(--fx-ink-200) 70%, transparent)",
            background: "var(--fx-s-overlay)",
          }}
        >
          {failureMode ? (
            <>
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-secondary"
                onClick={() => onOpenChange(false)}
              >
                {t("common.cancel", "Cancelar")}
              </button>
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                onClick={onRetry}
              >
                {t(
                  "onboarding.certificateAcquisition.fiscaloConfirm.retryButton",
                  "Reintentar",
                )}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-secondary"
                onClick={() => onOpenChange(false)}
                disabled={confirming}
              >
                {t("common.cancel", "Cancelar")}
              </button>
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                onClick={() =>
                  void onConfirm(
                    phone.trim() || undefined,
                    tradeNameValue.trim() || undefined,
                  )
                }
                disabled={confirming}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {confirming ? (
                  <>
                    <Loader2
                      className="animate-spin"
                      aria-hidden
                      style={{ width: 14, height: 14 }}
                    />
                    {t(
                      "onboarding.certificateAcquisition.fiscaloConfirm.confirming",
                    )}
                  </>
                ) : (
                  t(
                    "onboarding.certificateAcquisition.fiscaloConfirm.confirmButton",
                    "Confirmar y enviar",
                  )
                )}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const inputStyles: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid var(--fx-ink-200)",
  borderRadius: 8,
  background: "var(--fx-s-overlay)",
  fontFamily: "var(--fx-ff-sans)",
  fontSize: 14,
  color: "var(--fx-s-text)",
};

function ReadOnlyRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(140px, 36%) 1fr",
        gap: 16,
        padding: "12px 16px",
        borderBottom: "1px solid var(--fx-ink-200)",
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontFamily: "var(--fx-ff-mono)",
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          color: "var(--fx-ink-500, #6B7280)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 14,
          color: "var(--fx-s-text)",
          fontWeight: 500,
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function EditableRow({
  label,
  htmlFor,
  isLast = false,
  children,
}: {
  label: string;
  htmlFor: string;
  isLast?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(140px, 36%) 1fr",
        gap: 16,
        padding: "12px 16px",
        borderBottom: isLast ? "none" : "1px solid var(--fx-ink-200)",
        alignItems: "center",
        background: "var(--fx-s-overlay)",
      }}
    >
      <label
        htmlFor={htmlFor}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontFamily: "var(--fx-ff-mono)",
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          color: "var(--fx-ink-500, #6B7280)",
        }}
      >
        <Pencil
          aria-hidden
          style={{
            width: 11,
            height: 11,
            color: "var(--fx-accent-500)",
          }}
        />
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * Loading panel shown while handleConfirmFiscaloCertificate is creating
 * the cert request + polling the Abitab automation. Replaces the data
 * review so the user clearly sees background work is happening — the
 * previous version only flashed a small spinner inside the submit button
 * and the rest of the modal sat unchanged, which read as "stalled".
 */
function FiscaloAutomationProgress(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      style={{
        margin: "0 28px",
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "center",
        padding: "24px 0",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 22,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "grid",
            placeItems: "center",
            width: 56,
            height: 56,
            borderRadius: "50%",
            background:
              "color-mix(in srgb, var(--fx-accent-300) 18%, transparent)",
            color: "var(--fx-accent-500)",
            boxShadow:
              "0 0 0 8px color-mix(in srgb, var(--fx-accent-300) 10%, transparent)",
          }}
        >
          <Loader2
            className="animate-spin"
            aria-hidden
            style={{ width: 28, height: 28 }}
          />
        </span>
      </div>
      <p
        style={{
          margin: "0 auto",
          maxWidth: 360,
          textAlign: "center",
          fontSize: 14,
          lineHeight: 1.55,
          color: "var(--fx-s-text)",
        }}
      >
        {t(
          "onboarding.certificateAcquisition.fiscaloConfirm.progressHint",
          "Estamos generando tu solicitud en ID Digital. En unos segundos te damos el número que tenés que llevar a Abitab para completar el trámite.",
        )}
      </p>
    </div>
  );
}

/**
 * Failure panel — the cert request creation on our backend failed
 * (createCertificateRequest threw). Nothing was saved server-side, so
 * the user can safely retry. The Abitab automation call is fire-and-
 * forget and never surfaces a failure here.
 */
function FiscaloAutomationFailure(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      style={{
        margin: "0 28px",
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "center",
        padding: "24px 0",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 22,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "grid",
            placeItems: "center",
            width: 56,
            height: 56,
            borderRadius: "50%",
            background:
              "color-mix(in srgb, #C58F2E 18%, transparent)",
            color: "#C58F2E",
            boxShadow:
              "0 0 0 8px color-mix(in srgb, #C58F2E 8%, transparent)",
          }}
        >
          <AlertTriangle aria-hidden style={{ width: 28, height: 28 }} />
        </span>
      </div>
      <p
        style={{
          margin: "0 auto",
          maxWidth: 380,
          textAlign: "center",
          fontSize: 14,
          lineHeight: 1.55,
          color: "var(--fx-s-text)",
        }}
      >
        {t(
          "onboarding.certificateAcquisition.fiscaloConfirm.failureBodyFailed",
          "No pudimos guardar tu solicitud en este momento. Probá de nuevo — si el problema persiste, escribinos y un agente se ocupa.",
        )}
      </p>
    </div>
  );
}
